// src/services/thirteenthMonthService.js
//
// The 13th month generator (PRD_ThirteenthMonthPay). A pure core that turns a
// year's approved payroll lines into one row per employee, plus the one loader
// that reads its inputs. Nothing here writes — the dialog creates ordinary
// `thirteenth_month` adjustments through createAdjustmentsPerRow.

import { doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { toYMDString } from "../utils/periodDate";
import { currentPeriod } from "../utils/payCutoffs";
import { loadActiveLocks } from "./dtrLockDerivation";
import { loadRunEmployees } from "./payrollRunStore";
import {
  adjustmentsCollection,
  paidPeriodWarning,
  validateAmount,
} from "./adjustmentService";
import { normaliseThirteenthMonthBasis } from "../constants/thirteenthMonthBasis";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => Number(v) || 0;

// Whole days since the epoch for a 'YYYY-MM-DD' string — pure calendar math.
const serial = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};
const daysInclusive = (fromYMD, toYMD) =>
  fromYMD && toYMD && fromYMD <= toYMD ? serial(toYMD) - serial(fromYMD) + 1 : 0;
const nextDay = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 1)); // UTC in, UTC parts out — no zone shift
  return [
    t.getUTCFullYear(),
    String(t.getUTCMonth() + 1).padStart(2, "0"),
    String(t.getUTCDate()).padStart(2, "0"),
  ].join("-");
};
const maxYMD = (a, b) => (!a ? b : !b ? a : a > b ? a : b);
const minYMD = (a, b) => (!a ? b : !b ? a : a < b ? a : b);

// C2/C4 — how each factor reads a persisted line. Every value goes through
// num(): daily lines carry no `deductions.absence` (computeEarnings.js
// initEmployeeShapes omits it), and a literal `basic − undefined` is NaN.
const FACTOR_READ = {
  overtime: (l) => num(l.earnings?.overtime),
  nightDiff: (l) => num(l.earnings?.nightDiff),
  holiday: (l) => num(l.earnings?.holiday),
  restDay: (l) => num(l.earnings?.restDay),
  undertime: (l) => num(l.deductions?.undertime),
  absence: (l) => num(l.deductions?.absence),
};
const SUBTRACT = new Set(["undertime", "absence"]);

const lineBasis = (line, factors) =>
  Object.entries(FACTOR_READ).reduce(
    (sum, [key, read]) =>
      factors[key] ? sum + (SUBTRACT.has(key) ? -read(line) : read(line)) : sum,
    num(line.earnings?.basic)
  );

const keyOf = (line) => String(line.employeeId || line.id);

// C3 — an unclaimed entry dated inside a COMPLETED run for that employee can
// never be picked up. A pending-approval run is deliberately not enough: a
// rejected run returns to draft and its resume would still embed the entry.
const coveredByCompletedRun = (employeeId, dateYMD, runs) =>
  (runs || []).some(
    (r) =>
      r &&
      r.status === "completed" &&
      Array.isArray(r.employeeIds) &&
      r.employeeIds.map(String).includes(String(employeeId)) &&
      toYMDString(r.periodStart) <= dateYMD &&
      dateYMD <= toYMDString(r.periodEnd)
  );

/**
 * C5 — 15 December of `year`, placed inside the current pay cycle: the date
 * itself when the cycle holds it, else the cycle's end (15 Dec is later) or
 * start (15 Dec is earlier). Unclamped when the calendar cannot resolve.
 */
export const placeDefaultDate = (year, cutoffStartDays, todayYMD) => {
  const target = `${year}-12-15`;
  const cur = currentPeriod(cutoffStartDays, todayYMD);
  if (!cur) return target;
  if (target < cur.start) return cur.start;
  if (target > cur.end) return cur.end;
  return target;
};

/**
 * C1/C5 — the dialog's opening Year and Effective date. Year is the current
 * Manila year, except in January (the top-up month), when it is the previous.
 */
export const defaultYearAndDate = ({ todayYMD, cutoffStartDays }) => {
  const [y, m] = todayYMD.split("-").map(Number);
  const year = m === 1 ? y - 1 : y;
  return { year, effectiveDate: placeDefaultDate(year, cutoffStartDays, todayYMD) };
};

/** C7 — who sees the Generate 13th Month button. */
export const canGenerateThirteenthMonth = (hasPermission) =>
  Boolean(hasPermission("adj:edit") && hasPermission("payroll:view"));

/**
 * The pure core. Rows for every employee employed during `year` (C8).
 *
 * @param {Object} p
 * @param {number} p.year
 * @param {string} p.effectiveDate - 'YYYY-MM-DD'
 * @param {Object} p.factors - normalised basis factors (C4)
 * @param {Array} p.employees - the page projection { employeeId, fullName, startDate, endDate }
 * @param {Array} p.indexRuns - payrollIndex/master runs
 * @param {Object} p.linesByRunId - { runId: [persisted employee lines] } for the year's completed runs
 * @param {Array} p.adjustments - every `thirteenth_month` adjustment doc ({ id, ...data })
 * @returns {Array} rows
 */
export const computeThirteenthMonthRows = ({
  year, effectiveDate, factors, employees, indexRuns, linesByRunId, adjustments,
}) => {
  const yStart = `${year}-01-01`;
  const yEnd = `${year}-12-31`;

  // C8 — employed at some point in the year.
  const listed = (employees || []).filter(
    (e) => (!e.startDate || e.startDate <= yEnd) && (!e.endDate || e.endDate >= yStart)
  );
  const acc = new Map(
    listed.map((e) => [
      String(e.employeeId),
      { emp: e, basis: 0, days: 0, earliestStart: "", latestEnd: "", legacyGiven: 0 },
    ])
  );

  // C2/C9 — completed runs whose period ENDS in the year.
  const yearRuns = (indexRuns || []).filter(
    (r) => r && r.status === "completed" && toYMDString(r.periodEnd).slice(0, 4) === String(year)
  );
  yearRuns.forEach((run) => {
    const ps = toYMDString(run.periodStart);
    const pe = toYMDString(run.periodEnd);
    (linesByRunId[run.runId] || []).forEach((line) => {
      const a = acc.get(keyOf(line));
      if (!a) return; // a line for no listed employee is ignored
      a.basis += lineBasis(line, factors);
      a.days += daysInclusive(maxYMD(ps, a.emp.startDate), minYMD(pe, a.emp.endDate));
      a.earliestStart = a.earliestStart ? minYMD(a.earliestStart, ps) : ps;
      a.latestEnd = maxYMD(a.latestEnd, pe);
      // C3(b) — pre-Standalone wizard entries live only inside the line.
      (line.earnings?.adjustments || []).forEach((adj) => {
        if (adj.category === "thirteenth_month" && !adj.sourceId) a.legacyGiven += num(adj.amount);
      });
    });
  });

  const inDecember = effectiveDate.slice(0, 7) === `${year}-12`;

  return listed.map((e) => {
    const key = String(e.employeeId);
    const a = acc.get(key);

    // C14 — day-weighted projection, only for a December date or to a leaver's last day.
    const leaverAhead = Boolean(e.endDate) && e.endDate.slice(0, 4) === String(year) && e.endDate >= effectiveDate;
    let projected = 0;
    if ((inDecember || leaverAhead) && a.days > 0) {
      const projEnd = e.endDate && e.endDate <= yEnd ? e.endDate : yEnd;
      const projStart = maxYMD(nextDay(a.latestEnd), e.startDate);
      projected = (a.basis / a.days) * daysInclusive(projStart, projEnd);
    }
    const thirteenth = round2((a.basis + projected) / 12);

    // C3/C11 — already given.
    const stuck = [];
    let given = a.legacyGiven;
    (adjustments || []).forEach((adj) => {
      if (String(adj.employeeId) !== key || adj.metadata?.isDeleted) return;
      const dateYMD = toYMDString(adj.effectiveDate);
      const forYear = adj.thirteenthMonthYear ?? Number(dateYMD.slice(0, 4));
      if (forYear !== year) return;
      if (!adj.consumedRunId && coveredByCompletedRun(key, dateYMD, indexRuns)) {
        stuck.push({ id: adj.id, amount: num(adj.amount), effectiveDate: dateYMD });
        return;
      }
      given += num(adj.amount);
    });
    const alreadyGiven = round2(given);
    const computedToPay = Math.max(0, round2(thirteenth - alreadyGiven));

    // C15 — why a row cannot take an entry on this date.
    let blockReason = null;
    if (e.endDate && effectiveDate > e.endDate) {
      blockReason = { code: "separated", date: e.endDate };
    } else if (e.startDate && effectiveDate < e.startDate) {
      blockReason = { code: "notYetHired", date: e.startDate };
    } else if (paidPeriodWarning(key, effectiveDate, indexRuns)) {
      blockReason = { code: "alreadyPaid" };
    }

    return {
      employeeId: key,
      fullName: e.fullName,
      approvedBasis: round2(a.basis),
      projected: round2(projected),
      thirteenth,
      alreadyGiven,
      computedToPay,
      noApprovedRun: a.days === 0,
      // C13 — earnings before the first approved line are invisible to the basis.
      lateFirstRun:
        a.earliestStart && a.earliestStart > `${year}-01-31` && (!e.startDate || e.startDate < a.earliestStart)
          ? a.earliestStart
          : null,
      stuck,
      blockReason,
    };
  });
};

/** C15 — a row is tickable when its typed amount is valid and its date can take an entry. */
export const isTickable = (row, enteredAmount) =>
  !row.blockReason && validateAmount(enteredAmount) === null;

/**
 * C15 — default ticked from 1 December of the selected year onward (January
 * top-ups included), judged on the amount as entered when one was typed.
 */
export const isDefaultTicked = (row, effectiveDate, year, enteredAmount = row.computedToPay) =>
  isTickable(row, enteredAmount) && effectiveDate >= `${year}-12-01`;

/**
 * Reads everything the core needs. Any failure throws — the dialog shows its
 * load error rather than a silent ₱0 basis (C2).
 *
 * @param {string} companyId
 * @param {number} year
 * @returns {Promise<{ factors, indexRuns, linesByRunId, adjustments }>}
 */
export const loadThirteenthMonthInputs = async (companyId, year) => {
  const [indexRuns, ratesSnap] = await Promise.all([
    loadActiveLocks(companyId),
    getDoc(doc(db, "companies", companyId, "settings", "payrollRates")),
  ]);
  const factors = normaliseThirteenthMonthBasis(
    ratesSnap.exists() ? ratesSnap.data().thirteenthMonthBasis : undefined
  ).factors;

  const yearRuns = indexRuns.filter(
    (r) => r && r.status === "completed" && toYMDString(r.periodEnd).slice(0, 4) === String(year)
  );
  const [lineSets, adjSnap] = await Promise.all([
    Promise.all(
      yearRuns.map(async (r) => {
        const parentSnap = await getDoc(doc(db, "companies", companyId, "payrollRuns", r.runId));
        if (!parentSnap.exists()) {
          throw new Error(`Payroll run ${r.runId} is missing.`);
        }
        return [r.runId, await loadRunEmployees(companyId, r.runId, parentSnap.data())];
      })
    ),
    getDocs(query(adjustmentsCollection(companyId), where("category", "==", "thirteenth_month"))),
  ]);

  return {
    factors,
    indexRuns,
    linesByRunId: Object.fromEntries(lineSets),
    adjustments: adjSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
};
